import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { SymptomAnalysisInput } from "@/types/naturopathy";
import { Search, Plus, X, User, Calendar, Pill, Baby, Heart, AlertTriangle } from 'lucide-react';
import { symptomAnalysisSchema } from '@/lib/validation';
import { toast } from 'sonner';

interface SymptomAnalyzerProps {
  onAnalyze: (input: SymptomAnalysisInput) => void;
  isLoading?: boolean;
}

const SymptomAnalyzer: React.FC<SymptomAnalyzerProps> = ({ onAnalyze, isLoading = false }) => {
  const [symptoms, setSymptoms] = useState('');
  const [age, setAge] = useState<number>();
  const [gender, setGender] = useState<'homme' | 'femme'>();
  const [medications, setMedications] = useState<string[]>([]);
  const [allergies, setAllergies] = useState<string[]>([]);
  const [currentMedication, setCurrentMedication] = useState('');
  const [currentAllergy, setCurrentAllergy] = useState('');
  const [isPregnant, setIsPregnant] = useState(false);
  const [isBreastfeeding, setIsBreastfeeding] = useState(false);

  const suggestedSymptoms = [
    'Ballonnements', 'Fatigue', 'Stress', 'Troubles du sommeil', 'Douleurs articulaires',
    'Problèmes digestifs', 'Maux de tête', 'Anxiété', 'Troubles hormonaux', 'Acné'
  ];

  const suggestedMedications = [
    'Anti-inflammatoire', 'Paracétamol', 'Anticoagulant', 'Antidépresseur', 'Anxiolytique',
    'Antihypertenseur', 'Antidiabétique', 'Thyroïde', 'Contraceptif', 'Statine'
  ];

  const suggestedAllergies = [
    'Astéracées', 'Apiacées', 'Latex', 'Propolis', 'Pollen', 'Salicylés', 'Fruits à coque'
  ];

  const handleAddMedication = () => {
    if (currentMedication.trim() && !medications.includes(currentMedication.trim())) {
      setMedications([...medications, currentMedication.trim()]);
      setCurrentMedication('');
    }
  };

  const handleAddAllergy = () => {
    if (currentAllergy.trim() && !allergies.includes(currentAllergy.trim())) {
      setAllergies([...allergies, currentAllergy.trim()]);
      setCurrentAllergy('');
    }
  };

  const removeMedication = (med: string) => {
    setMedications(medications.filter(m => m !== med));
  };

  const removeAllergy = (allergy: string) => {
    setAllergies(allergies.filter(a => a !== allergy));
  };

  const addSuggestedSymptom = (symptom: string) => {
    const currentText = symptoms.trim();
    if (currentText && !currentText.endsWith(',') && !currentText.endsWith(' ')) {
      setSymptoms(currentText + ', ' + symptom);
    } else {
      setSymptoms(currentText + symptom);
    }
  };

  const handleAnalyze = () => {
    if (!symptoms.trim()) return;
    
    const input: SymptomAnalysisInput = {
      symptoms: symptoms.trim(),
      age,
      gender,
      medications: medications.length > 0 ? medications : undefined,
      allergies: allergies.length > 0 ? allergies : undefined,
      isPregnant: gender === 'femme' ? isPregnant : undefined,
      isBreastfeeding: gender === 'femme' ? isBreastfeeding : undefined,
    };

    // Validate input
    const validation = symptomAnalysisSchema.safeParse(input);
    if (!validation.success) {
      const errors = validation.error.errors.map(e => e.message).join(', ');
      toast.error('Validation échouée', {
        description: errors
      });
      return;
    }

    onAnalyze(input);
  };

  return (
    <Card className="w-full max-w-4xl mx-auto shadow-card bg-gradient-card">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl text-primary flex items-center gap-2">
          <Search className="w-6 h-6" />
          Analyse Naturopathique
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Décrivez les symptômes ou problématiques à analyser
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Saisie des symptômes */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Symptômes et problématiques</label>
          <Textarea
            value={symptoms}
            onChange={(e) => setSymptoms(e.target.value)}
            placeholder="Ex: ballonnements, fatigue chronique, troubles du sommeil depuis 3 mois..."
            className="min-h-[100px] resize-none"
          />
          
          {/* Suggestions */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Suggestions rapides :</p>
            <div className="flex flex-wrap gap-2">
              {suggestedSymptoms.map((symptom) => (
                <Button
                  key={symptom}
                  variant="outline"
                  size="sm"
                  onClick={() => addSuggestedSymptom(symptom)}
                  className="h-8 text-xs"
                >
                  {symptom}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <Separator />

        {/* Informations patient */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Âge
            </label>
            <Input
              type="number"
              value={age || ''}
              onChange={(e) => setAge(e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="Ex: 35"
              className="w-full"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <User className="w-4 h-4" />
              Sexe
            </label>
            <Select 
              value={gender || ''} 
              onValueChange={(value: 'homme' | 'femme') => {
                setGender(value);
                // Reset pregnancy/breastfeeding if not female
                if (value !== 'femme') {
                  setIsPregnant(false);
                  setIsBreastfeeding(false);
                }
              }}
            >
              <SelectTrigger onPointerDown={(e) => e.stopPropagation()} translate="no">
                <SelectValue placeholder="Choisir" />
              </SelectTrigger>
              <SelectContent translate="no">
                <SelectItem value="femme">Femme</SelectItem>
                <SelectItem value="homme">Homme</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* États physiologiques (uniquement pour les femmes) */}
        {gender === 'femme' && (
          <div className="p-4 bg-pink-50 dark:bg-pink-950/30 border border-pink-200 dark:border-pink-800 rounded-lg space-y-3">
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" />
              État physiologique
            </label>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="pregnant" 
                  checked={isPregnant}
                  onCheckedChange={(checked) => setIsPregnant(checked === true)}
                />
                <label htmlFor="pregnant" className="text-sm cursor-pointer flex items-center gap-1">
                  <Baby className="w-4 h-4 text-pink-500" />
                  Enceinte
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="breastfeeding" 
                  checked={isBreastfeeding}
                  onCheckedChange={(checked) => setIsBreastfeeding(checked === true)}
                />
                <label htmlFor="breastfeeding" className="text-sm cursor-pointer flex items-center gap-1">
                  <Baby className="w-4 h-4 text-pink-500" />
                  Allaitante
                </label>
              </div>
            </div>
            {(isPregnant || isBreastfeeding) && (
              <p className="text-xs text-pink-600 dark:text-pink-400">
                ⚠️ Les recommandations seront adaptées avec des précautions spécifiques.
              </p>
            )}
          </div>
        )}

        {/* Médicaments */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Pill className="w-4 h-4" />
            Médicaments actuels
          </label>
          <div className="flex gap-2">
            <Input
              value={currentMedication}
              onChange={(e) => setCurrentMedication(e.target.value)}
              placeholder="Nom ou type de médicament (ex: anti-inflammatoire, paracétamol...)"
              onKeyPress={(e) => e.key === 'Enter' && handleAddMedication()}
            />
            <Button onClick={handleAddMedication} variant="outline" size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {/* Suggestions de médicaments */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Types courants :</p>
            <div className="flex flex-wrap gap-1">
              {suggestedMedications.map((med) => (
                <Button
                  key={med}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!medications.includes(med)) {
                      setMedications([...medications, med]);
                    }
                  }}
                  className="h-6 text-xs px-2 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                  disabled={medications.includes(med)}
                >
                  + {med}
                </Button>
              ))}
            </div>
          </div>
          {medications.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {medications.map((med) => (
                <Badge key={med} variant="secondary" className="gap-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200">
                  <Pill className="w-3 h-3" />
                  {med}
                  <X 
                    className="w-3 h-3 cursor-pointer hover:text-destructive" 
                    onClick={() => removeMedication(med)}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Allergies */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Allergies connues
          </label>
          <div className="flex gap-2">
            <Input
              value={currentAllergy}
              onChange={(e) => setCurrentAllergy(e.target.value)}
              placeholder="Allergie ou intolérance (ex: astéracées, propolis, latex...)"
              onKeyPress={(e) => e.key === 'Enter' && handleAddAllergy()}
            />
            <Button onClick={handleAddAllergy} variant="outline" size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {/* Suggestions d'allergies */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Allergies courantes :</p>
            <div className="flex flex-wrap gap-1">
              {suggestedAllergies.map((allergy) => (
                <Button
                  key={allergy}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!allergies.includes(allergy)) {
                      setAllergies([...allergies, allergy]);
                    }
                  }}
                  className="h-6 text-xs px-2 bg-orange-50 dark:bg-orange-950/30 hover:bg-orange-100 dark:hover:bg-orange-900/40 text-orange-700 dark:text-orange-300"
                  disabled={allergies.includes(allergy)}
                >
                  + {allergy}
                </Button>
              ))}
            </div>
          </div>
          {allergies.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {allergies.map((allergy) => (
                <Badge key={allergy} variant="secondary" className="gap-2 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200">
                  <AlertTriangle className="w-3 h-3" />
                  {allergy}
                  <X 
                    className="w-3 h-3 cursor-pointer hover:text-destructive" 
                    onClick={() => removeAllergy(allergy)}
                  />
                </Badge>
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* Bouton d'analyse */}
        <Button 
          onClick={handleAnalyze}
          disabled={!symptoms.trim() || isLoading}
          className="w-full bg-gradient-primary text-primary-foreground hover:opacity-90 h-12"
        >
          {isLoading ? 'Analyse en cours...' : 'Analyser les symptômes'}
        </Button>

        {/* Disclaimer */}
        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
          ⚠️ <strong>Important :</strong> Cette analyse fournit des conseils naturopathiques complémentaires et ne remplace pas un suivi médical. 
          En cas de symptômes graves ou persistants, consultez un professionnel de santé.
        </div>
      </CardContent>
    </Card>
  );
};

export default SymptomAnalyzer;